import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import omit from 'lodash/omit';
import { useContext, useState } from 'react';
import { Card } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { ApiError, AuthService, PasswordResetConfirm } from '../api';
import { Logo } from '../app/components/Logo';
import { PasswordField } from '../app/components/PasswordField';
import { ServerErrorsAlert } from '../app/components/ServerErrorsAlert';
import { FormSubmitContext } from '../app/contexts/FormSubmitContext';
import { useDocumentTitle } from '../app/hooks/use-document-title';
import { generateShowFieldErrors } from '../utils/forms';
import { strongPasswordZodSchema } from '../utils/security';

const validationSchema = z
  .object({
    new_password1: strongPasswordZodSchema,
    new_password2: z.string().min(1, { message: 'Confirm Password is required' }),
  })
  .refine(({ new_password1, new_password2 }) => new_password1 === new_password2, {
    path: ['new_password2'],
    message: "Your passwords don't match.",
  });

export const PasswordReset = () => {
  useDocumentTitle('Password reset');
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const { uid, token } = useParams();

  const {
    handleSubmit,
    formState: { errors },
    register,
    clearErrors,
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [serverErrors, setServerErrors] = useState<ApiError>();
  const [showGlobalServerErrors, setShowGlobalErrors] = useState(false);
  const [showServerFieldErrors, setShowServerFieldErrors] = useState<{
    new_password1?: string;
    new_password2?: string;
  }>({});

  // Callback for clearing alerts received from server
  function handleClearErrorAlert() {
    setShowGlobalErrors(false);
  }

  const handleSubmitClick = () => {
    clearErrors();
    setShowGlobalErrors(false);
    setShowServerFieldErrors({});
  };

  const mutationPasswordResetConfirm = useMutation({
    mutationFn: async (data: PasswordResetConfirm) => {
      await AuthService.authPasswordResetConfirmCreate({ requestBody: data });
    },
  });

  // Callback for registration form submission
  const onSubmit = async (data) => {
    try {
      await mutationPasswordResetConfirm.mutateAsync({
        ...data,
        uid,
        token,
      });
      setShowSuccess(true);
    } catch (errors) {
      if (errors.status === 400) {
        const fieldErrors = omit(errors.body, ['non_field_errors', 'uid', 'token']);
        setShowServerFieldErrors(generateShowFieldErrors(fieldErrors));
        setServerErrors(errors);
        setShowGlobalErrors(true);
      }
    }
  };

  // Registration page
  return (
    <div className="login-bg-container">
      <Container>
        <Card>
          <Card.Body>
            <Alert show={showSuccess} variant="success">
              <Alert.Heading>Password has been reset successfully</Alert.Heading>
              <p>You can safely use it to login.</p>
            </Alert>

            {showSuccess && (
              <div className="text-center mt-4">
                <Button variant="outline-primary" href="/login">
                  Back to Login
                </Button>
              </div>
            )}

            {!showSuccess && (
              <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                <div className="logo-container">
                  <Logo className="logo" variant="color" />
                </div>
                <h1 className="text-center mb-4">Reset your password</h1>
                <ServerErrorsAlert
                  errors={serverErrors}
                  showErrors={showGlobalServerErrors}
                  clearErrors={handleClearErrorAlert}
                  className="my-3"
                />
                <PasswordField
                  name="new_password1"
                  label=""
                  placeholder="Enter new password"
                  errors={errors}
                  register={register}
                  required
                />
                <PasswordField
                  name="new_password2"
                  label=""
                  placeholder="Confirm new password"
                  errors={errors}
                  register={register}
                  required
                />
                <div className="d-grid mt-4">
                  <Button
                    size="lg"
                    className="btn-shadow w-100"
                    type="submit"
                    variant="primary"
                    onClick={() => handleSubmitClick()}
                    disabled={mutationPasswordResetConfirm.isLoading || !isSubmitEnabled}
                  >
                    Reset Password
                  </Button>
                </div>
              </Form>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};
